import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Plus, X } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { Label } from "@/clientWeb/components/ui/label"
import { Badge } from "@/clientWeb/components/ui/badge"
import type { StepProps } from "./types"

const MAX_FIELDS = 10

export function StepRegistration({ data, onChange }: StepProps) {
  const t = useTranslation()
  const [newFieldName, setNewFieldName] = useState("")
  const [addFieldOpen, setAddFieldOpen] = useState(false)
  const [domainInput, setDomainInput] = useState("")
  const [domainErr, setDomainErr] = useState("")

  const handleWithRegistrationChange = (val: boolean) => {
    onChange({
      withRegistration: val,
      registrationFields: val
        ? data.registrationFields
        : [{ field: "Full Name", required: true }, { field: "Email", required: true }],
    })
  }

  const handleAddField = () => {
    const name = newFieldName.trim()
    if (!name) return
    if (data.registrationFields.length >= MAX_FIELDS) return
    onChange({ registrationFields: [...data.registrationFields, { field: name, required: false }] })
    setNewFieldName("")
    setAddFieldOpen(false)
  }

  const handleRemoveField = (idx: number) =>
    onChange({ registrationFields: data.registrationFields.filter((_, i) => i !== idx) })

  const handleFieldRequired = (idx: number, required: boolean) =>
    onChange({
      registrationFields: data.registrationFields.map((f, i) => i === idx ? { ...f, required } : f),
    })

  const handleAddDomain = () => {
    const pattern = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/
    if (!pattern.test(domainInput)) { setDomainErr("Enter a valid domain (e.g. example.com)"); return }
    const list = data.domainRestrictionType === 1 ? data.authorizedDomains : data.restrictedDomains
    if (list.includes(domainInput)) { setDomainErr("Domain already added"); return }
    if (data.domainRestrictionType === 1) {
      onChange({ authorizedDomains: [...data.authorizedDomains, domainInput] })
    } else {
      onChange({ restrictedDomains: [...data.restrictedDomains, domainInput] })
    }
    setDomainInput("")
    setDomainErr("")
  }

  const handleRemoveDomain = (domain: string) => {
    if (data.domainRestrictionType === 1) {
      onChange({ authorizedDomains: data.authorizedDomains.filter((d) => d !== domain) })
    } else {
      onChange({ restrictedDomains: data.restrictedDomains.filter((d) => d !== domain) })
    }
  }

  const activeChips = data.domainRestrictionType === 1 ? data.authorizedDomains : data.restrictedDomains

  return (
    <div className="space-y-5">
      {/* Registration Setup */}
      <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-3">
        <div>
          <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.registrationSetup")}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.chooseHowAttendeesAccess")}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <label className={`flex-1 flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-colors ${data.withRegistration ? "border-primary bg-primary/5" : "border-stroke-100 dark:border-border dark:border-border"}`}>
            <input type="radio" name="regtype" value="with" checked={data.withRegistration}
              onChange={() => handleWithRegistrationChange(true)} className="mt-0.5 accent-primary" />
            <div>
              <p className="font-medium text-sm text-slate-800 dark:text-white">{t("webinar.withRegistration")}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.attendeesFillForm")}</p>
            </div>
          </label>
          <label className={`flex-1 flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-colors ${!data.withRegistration ? "border-primary bg-primary/5" : "border-stroke-100 dark:border-border dark:border-border"}`}>
            <input type="radio" name="regtype" value="without" checked={!data.withRegistration}
              onChange={() => handleWithRegistrationChange(false)} className="mt-0.5 accent-primary" />
            <div>
              <p className="font-medium text-sm text-slate-800 dark:text-white">{t("webinar.withoutRegistration")}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.anyoneWithLinkJoin")}</p>
            </div>
          </label>
        </div>
      </div>

      {/* Registration Fields – only shown when withRegistration */}
      {data.withRegistration && (
        <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.registrationFields")}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.infoCollectedFromAttendees")} {MAX_FIELDS}</p>
            </div>
            {data.registrationFields.length < MAX_FIELDS && (
              <Button type="button" variant="outline" size="sm" onClick={() => setAddFieldOpen(!addFieldOpen)}>
                <Plus className="w-4 h-4 mr-1" /> {t("webinar.addField")}
              </Button>
            )}
          </div>
          {addFieldOpen && (
            <div className="flex gap-2">
              <Input value={newFieldName} onChange={(e) => setNewFieldName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddField()}
                placeholder={t("webinar.fieldNamePlaceholder")}
                className="flex-1 h-10" />
              <Button type="button" size="sm" onClick={handleAddField} className="h-10">{t("webinar.add")}</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setAddFieldOpen(false); setNewFieldName("") }} className="h-10">
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          <div className="space-y-2">
            {data.registrationFields.map((field, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-stroke-100 dark:border-border dark:border-border bg-slate-50/50 dark:bg-card/30">
                <span className="flex-1 text-sm text-slate-700 dark:text-slate-200">{field.field}</span>
                <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 cursor-pointer">
                  <input type="checkbox" checked={field.required} disabled={idx < 2}
                    onChange={(e) => handleFieldRequired(idx, e.target.checked)}
                    className="accent-primary" />
                  {t("webinar.required")}
                </label>
                {idx > 1 && (
                  <button type="button" onClick={() => handleRemoveField(idx)}
                    className="text-slate-400 hover:text-red-500 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Domain Restriction */}
      <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-3">
        <div className="flex items-start gap-3">
          <input type="checkbox" id="domainCheck" checked={data.domainRestrictionEnabled}
            onChange={(e) => onChange({ domainRestrictionEnabled: e.target.checked, authorizedDomains: [], restrictedDomains: [] })}
            className="mt-0.5 accent-primary cursor-pointer" />
          <label htmlFor="domainCheck" className="cursor-pointer">
            <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white text-sm">{t("webinar.emailDomainRestrictions")}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {data.domainRestrictionEnabled ? t("webinar.domainRestrictionActive") : t("webinar.noDomainRestriction")}
            </p>
          </label>
        </div>

        {data.domainRestrictionEnabled && (
          <div className="space-y-3 pt-1">
            <select
              value={data.domainRestrictionType}
              onChange={(e) => onChange({ domainRestrictionType: parseInt(e.target.value) as 1 | 2, authorizedDomains: [], restrictedDomains: [] })}
              className="w-full sm:w-64 h-10 rounded-lg border border-stroke-100 dark:border-border dark:border-border bg-card dark:bg-card text-slate-900 dark:text-foreground/80 dark:text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value={1}>{t("webinar.allowAuthorizedOnly")}</option>
              <option value={2}>{t("webinar.restrictPublicDomains")}</option>
            </select>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {data.domainRestrictionType === 1
                ? t("webinar.onlyListedDomainsRegister")
                : t("webinar.listedDomainsBlocked")}
            </p>
            <div className="flex gap-2">
              <Input value={domainInput}
                onChange={(e) => { setDomainInput(e.target.value.toLowerCase()); setDomainErr("") }}
                onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
                placeholder={data.domainRestrictionType === 1 ? "example.com" : "gmail.com"}
                className="flex-1 h-10" maxLength={100} />
              <Button type="button" size="sm" onClick={handleAddDomain} className="h-10 px-4">{t("webinar.add")}</Button>
            </div>
            {domainErr && <p className="text-red-500 text-xs">{domainErr}</p>}
            {activeChips.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {activeChips.map((chip) => (
                  <Badge key={chip} variant="secondary" className="pr-1.5 py-1 text-xs">
                    {chip}
                    <button type="button" onClick={() => handleRemoveDomain(chip)} className="ml-1.5 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Moderation – only with registration */}
      {data.withRegistration && (
        <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-3">
          <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.moderation")}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <label className={`flex-1 flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-colors ${data.automaticMailApproval ? "border-primary bg-primary/5" : "border-stroke-100 dark:border-border dark:border-border"}`}>
              <input type="radio" name="moderation" checked={data.automaticMailApproval}
                onChange={() => onChange({ automaticMailApproval: true })} className="mt-0.5 accent-primary" />
              <div>
                <p className="font-medium text-sm text-slate-800 dark:text-white">{t("webinar.automaticApproval")}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.registrantsReceiveAutoConfirm")}</p>
              </div>
            </label>
            <label className={`flex-1 flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-colors ${!data.automaticMailApproval ? "border-primary bg-primary/5" : "border-stroke-100 dark:border-border dark:border-border"}`}>
              <input type="radio" name="moderation" checked={!data.automaticMailApproval}
                onChange={() => onChange({ automaticMailApproval: false })} className="mt-0.5 accent-primary" />
              <div>
                <p className="font-medium text-sm text-slate-800 dark:text-white">{t("webinar.manualApproval")}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.organizerApprovesIndividually")}</p>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Registration Limit – only with registration */}
      {data.withRegistration && (
        <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-3">
          <div>
            <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.registrationLimit")}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.maxRegistrantsAllowed")}</p>
          </div>
          <div>
            <Label htmlFor="reglimit" className="sr-only">Registration Limit</Label>
            <Input id="reglimit" type="number" min={4} max={500} value={data.registrationLimit}
              onChange={(e) => onChange({ registrationLimit: parseInt(e.target.value) || 4 })}
              className="h-11 max-w-xs" />
          </div>
        </div>
      )}
    </div>
  )
}
